import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  AGING_BUCKETS,
  formatDate,
  formatNumber,
  type Locale,
  type ReceivablesRow,
  type RoutePerformanceRow,
  type SalesByCustomerRow,
  type SalesByProductRow,
} from '@fdt/shared';
import {
  Box,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reports, type Range } from '../api/reports';
import { ExportButton } from '../components/ExportButton';
import { MoneyText } from '../components/MoneyText';
import { PageHeader } from '../components/PageHeader';
import { RangePicker, presetRange } from '../components/RangePicker';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { currentLocale } from '../i18n';
import { fetchCategories } from '../resources';

type TabKey = 'salesByProduct' | 'salesByCustomer' | 'receivables' | 'routePerformance';
const TABS: TabKey[] = ['salesByProduct', 'salesByCustomer', 'receivables', 'routePerformance'];

/** The same colours the routes list uses, so a status reads the same everywhere. */
const STATUS_COLOUR: Record<RoutePerformanceRow['status'], 'default' | 'info' | 'warning' | 'success'> = {
  draft: 'default',
  assigned: 'info',
  started: 'warning',
  completed: 'success',
};

/**
 * The four reports, behind tabs.
 *
 * One page rather than four routes, because they are read together: "who owes
 * me" is the question that follows "what did I sell", and a tab keeps the
 * window and the scroll position between them. The tab lives in the URL so a
 * particular report can be linked to or reloaded onto.
 *
 * The window is shared across the three tabs that have one. Receivables does
 * not — it is what is owed now (D120) — and the picker is hidden on that tab
 * rather than shown and ignored.
 */
export function ReportsPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = (TABS.find((key) => key === searchParams.get('tab')) ?? 'salesByProduct') as TabKey;
  const [range, setRange] = useState<Range>(() => presetRange('last30'));
  const [categoryId, setCategoryId] = useState('');
  const [debtorsOnly, setDebtorsOnly] = useState(true);

  const categoryList = useQuery({
    queryKey: ['product-categories'],
    queryFn: fetchCategories,
    staleTime: 300_000,
    enabled: tab === 'salesByProduct',
  });

  const byProduct = useQuery({
    queryKey: ['report', 'sales-by-product', range.from, range.to, categoryId],
    queryFn: () => reports.salesByProduct(range, categoryId || undefined),
    enabled: tab === 'salesByProduct',
  });

  const byCustomer = useQuery({
    queryKey: ['report', 'sales-by-customer', range.from, range.to],
    queryFn: () => reports.salesByCustomer(range),
    enabled: tab === 'salesByCustomer',
  });

  const receivables = useQuery({
    queryKey: ['report', 'receivables', debtorsOnly],
    queryFn: () => reports.receivables(debtorsOnly),
    enabled: tab === 'receivables',
  });

  const routePerformance = useQuery({
    queryKey: ['report', 'route-performance', range.from, range.to],
    queryFn: () => reports.routePerformance(range),
    enabled: tab === 'routePerformance',
  });

  const exportParams = { from: range.from, to: range.to };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { lg: 'center' } }}
      >
        <PageHeader icon={AssessmentIcon} tone="success" title={t('nav.reports')} />
        {tab !== 'receivables' && <RangePicker value={range} onChange={setRange} />}
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_, next: TabKey) => setSearchParams({ tab: next })}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map((key) => (
            <Tab key={key} value={key} label={t(`reports.tabs.${key}`)} />
          ))}
        </Tabs>
      </Box>

      {tab === 'salesByProduct' && (
        <ReportTable<SalesByProductRow>
          rows={byProduct.data?.rows ?? []}
          rowKey={(row) => row.productId}
          loading={byProduct.isPending}
          error={byProduct.error}
          truncated={byProduct.data?.truncated}
          defaultSortBy="net"
          toolbar={
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                select
                label={t('fields.category')}
                value={categoryId}
                onChange={(event) => setCategoryId(event.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {(categoryList.data ?? []).map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ ml: 'auto' }}>
                <ExportButton
                  file="sales-by-product.xlsx"
                  params={{ ...exportParams, categoryId: categoryId || undefined }}
                />
              </Box>
            </Stack>
          }
          columns={[
            {
              id: 'sku',
              label: t('fields.sku'),
              render: (row) => row.sku,
              sortValue: (row) => row.sku,
              width: 120,
            },
            {
              id: 'name',
              label: t('fields.product'),
              render: (row) => row.name,
              sortValue: (row) => row.name,
            },
            {
              id: 'category',
              label: t('fields.category'),
              render: (row) => row.categoryName ?? '—',
              sortValue: (row) => row.categoryName,
            },
            quantityColumn('sold', t('reports.product.sold'), (row) => row.soldQuantity, locale),
            quantityColumn(
              'returned',
              t('reports.product.returned'),
              (row) => row.returnedQuantity,
              locale,
            ),
            quantityColumn(
              'netQuantity',
              t('reports.product.netQuantity'),
              (row) => row.netQuantity,
              locale,
            ),
            moneyColumn('salesNet', t('reports.dashboard.salesNet'), (row) => row.salesNet),
            moneyColumn('returnsNet', t('reports.dashboard.returnsNet'), (row) => row.returnsNet),
            moneyColumn('net', t('reports.dashboard.net'), (row) => row.net),
            {
              id: 'documentCount',
              label: t('accounts.documentCount'),
              align: 'right',
              render: (row) => row.documentCount,
              sortValue: (row) => row.documentCount,
              total: (rows) => sumInts(rows.map((row) => row.documentCount), locale),
            },
          ]}
        />
      )}

      {tab === 'salesByCustomer' && (
        <ReportTable<SalesByCustomerRow>
          rows={byCustomer.data?.rows ?? []}
          rowKey={(row) => row.customerId}
          loading={byCustomer.isPending}
          error={byCustomer.error}
          truncated={byCustomer.data?.truncated}
          defaultSortBy="net"
          onRowClick={(row) => navigate(`/customers/${row.customerId}/account`)}
          toolbar={
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ExportButton file="sales-by-customer.xlsx" params={exportParams} />
            </Box>
          }
          columns={[
            {
              id: 'code',
              label: t('fields.code'),
              render: (row) => row.code,
              sortValue: (row) => row.code,
              width: 110,
            },
            {
              id: 'businessName',
              label: t('fields.businessName'),
              render: (row) => row.businessName,
              sortValue: (row) => row.businessName,
            },
            {
              id: 'comune',
              label: t('fields.comune'),
              render: (row) => row.comune ?? '—',
              sortValue: (row) => row.comune,
            },
            {
              id: 'documentCount',
              label: t('accounts.documentCount'),
              align: 'right',
              render: (row) => row.documentCount,
              sortValue: (row) => row.documentCount,
              total: (rows) => sumInts(rows.map((row) => row.documentCount), locale),
            },
            moneyColumn('salesNet', t('reports.dashboard.salesNet'), (row) => row.salesNet),
            moneyColumn('returnsNet', t('reports.dashboard.returnsNet'), (row) => row.returnsNet),
            moneyColumn('net', t('reports.dashboard.net'), (row) => row.net),
            moneyColumn(
              'collected',
              t('reports.customer.collectedInRange'),
              (row) => row.collected,
            ),
            {
              ...moneyColumn('balance', t('reports.customer.balanceNow'), (row) => row.balance),
              // Signed, like every other balance in the panel: red is owed.
              render: (row) => <MoneyText value={row.balance} signed />,
            },
          ]}
        />
      )}

      {tab === 'receivables' && (
        <ReportTable<ReceivablesRow>
          rows={receivables.data?.rows ?? []}
          rowKey={(row) => row.customerId}
          loading={receivables.isPending}
          error={receivables.error}
          truncated={receivables.data?.truncated}
          defaultSortBy="balance"
          note={t('reports.aging.note')}
          onRowClick={(row) => navigate(`/customers/${row.customerId}/account`)}
          toolbar={
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('reports.asOf')}:{' '}
                {receivables.data ? formatDate(receivables.data.range.to, locale) : '—'}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={debtorsOnly}
                    onChange={(event) => setDebtorsOnly(event.target.checked)}
                  />
                }
                label={t('reports.aging.debtorsOnly')}
              />
              <Box sx={{ ml: 'auto' }}>
                <ExportButton
                  file="receivables.xlsx"
                  params={{ debtorsOnly: String(debtorsOnly) }}
                />
              </Box>
            </Stack>
          }
          columns={[
            {
              id: 'code',
              label: t('fields.code'),
              render: (row) => row.code,
              sortValue: (row) => row.code,
              width: 110,
            },
            {
              id: 'businessName',
              label: t('fields.businessName'),
              render: (row) => row.businessName,
              sortValue: (row) => row.businessName,
            },
            {
              id: 'phone',
              label: t('fields.phone'),
              render: (row) => row.phone ?? '—',
              sortValue: (row) => row.phone,
            },
            {
              ...moneyColumn('balance', t('accounts.balance'), (row) => row.balance),
              render: (row) => <MoneyText value={row.balance} signed />,
            },
            moneyColumn('unpaidTotal', t('reports.aging.unpaid'), (row) => row.unpaidTotal),
            ...AGING_BUCKETS.map((bucket) =>
              moneyColumn<ReceivablesRow>(
                bucket,
                t(`reports.aging.buckets.${bucket}`),
                (row) => row.buckets[bucket] ?? '0.00',
              ),
            ),
            {
              id: 'oldestUnpaidOn',
              label: t('reports.aging.oldest'),
              align: 'right',
              render: (row) => (row.oldestUnpaidOn ? formatDate(row.oldestUnpaidOn, locale) : '—'),
              sortValue: (row) => row.oldestUnpaidOn,
            },
            {
              id: 'lastPaymentOn',
              label: t('reports.aging.lastPayment'),
              align: 'right',
              render: (row) => (row.lastPaymentOn ? formatDate(row.lastPaymentOn, locale) : '—'),
              sortValue: (row) => row.lastPaymentOn,
            },
          ]}
        />
      )}

      {tab === 'routePerformance' && (
        <ReportTable<RoutePerformanceRow>
          rows={routePerformance.data?.rows ?? []}
          rowKey={(row) => row.routeId}
          loading={routePerformance.isPending}
          error={routePerformance.error}
          truncated={routePerformance.data?.truncated}
          defaultSortBy="routeDate"
          onRowClick={(row) => navigate(`/routes/${row.routeId}`)}
          toolbar={
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ExportButton file="route-performance.xlsx" params={exportParams} />
            </Box>
          }
          columns={[
            {
              id: 'routeDate',
              label: t('fields.date'),
              render: (row) => formatDate(row.routeDate, locale),
              sortValue: (row) => row.routeDate,
              width: 120,
            },
            {
              id: 'name',
              label: t('fields.name'),
              render: (row) => row.name ?? '—',
              sortValue: (row) => row.name,
            },
            {
              id: 'status',
              label: t('fields.status'),
              render: (row) => (
                <Chip
                  size="small"
                  color={STATUS_COLOUR[row.status]}
                  label={t(`routes.statuses.${row.status}`)}
                />
              ),
              sortValue: (row) => row.status,
            },
            {
              id: 'driverName',
              label: t('routes.assignee'),
              render: (row) => row.driverName ?? '—',
              sortValue: (row) => row.driverName,
            },
            {
              id: 'vehiclePlate',
              label: t('fields.vehicle'),
              render: (row) => row.vehiclePlate ?? '—',
              sortValue: (row) => row.vehiclePlate,
            },
            {
              id: 'visited',
              label: t('routes.stops'),
              align: 'right',
              render: (row) => t('routes.visitedOf', { visited: row.visited, total: row.stops }),
              sortValue: (row) => row.visited,
              total: (rows) => sumInts(rows.map((row) => row.visited), locale),
            },
            {
              id: 'skipped',
              label: t('reports.route.skipped'),
              align: 'right',
              render: (row) => row.skipped || '—',
              sortValue: (row) => row.skipped,
              total: (rows) => sumInts(rows.map((row) => row.skipped), locale),
            },
            {
              id: 'plannedDistanceMeters',
              label: t('reports.route.plannedKm'),
              align: 'right',
              render: (row) =>
                row.plannedDistanceMeters === null
                  ? '—'
                  : formatNumber(row.plannedDistanceMeters / 1000, locale, 1),
              sortValue: (row) => row.plannedDistanceMeters,
              total: (rows) =>
                formatNumber(
                  rows.reduce((sum, row) => sum + (row.plannedDistanceMeters ?? 0), 0) / 1000,
                  locale,
                  1,
                ),
            },
            {
              id: 'durationMinutes',
              label: t('reports.route.durationMin'),
              align: 'right',
              render: (row) => row.durationMinutes ?? '—',
              sortValue: (row) => row.durationMinutes,
            },
            {
              id: 'documentCount',
              label: t('accounts.documentCount'),
              align: 'right',
              render: (row) => row.documentCount,
              sortValue: (row) => row.documentCount,
              total: (rows) => sumInts(rows.map((row) => row.documentCount), locale),
            },
            moneyColumn('net', t('reports.dashboard.net'), (row) => row.net),
            moneyColumn('collected', t('reports.route.collected'), (row) => row.collected),
          ]}
        />
      )}
    </Stack>
  );
}

/** A money column, right-aligned, sorted numerically and totalled in the footer. */
function moneyColumn<TRow>(
  id: string,
  label: string,
  value: (row: TRow) => string,
): ReportColumn<TRow> {
  return {
    id,
    label,
    align: 'right',
    render: (row) => <MoneyText value={value(row)} />,
    sortValue: (row) => Number.parseFloat(value(row)),
    total: (rows) => (
      <MoneyText
        // Summed as floats only for display; every stored figure is still a
        // string, and the server's own totals are the ones that are authoritative.
        value={rows.reduce((sum, row) => sum + Number.parseFloat(value(row)), 0).toFixed(2)}
      />
    ),
  };
}

function quantityColumn<TRow>(
  id: string,
  label: string,
  value: (row: TRow) => string,
  locale: Locale,
): ReportColumn<TRow> {
  return {
    id,
    label,
    align: 'right',
    render: (row) => formatNumber(Number.parseFloat(value(row)), locale, 2),
    sortValue: (row) => Number.parseFloat(value(row)),
    total: (rows) =>
      formatNumber(
        rows.reduce((sum, row) => sum + Number.parseFloat(value(row)), 0),
        locale,
        2,
      ),
  };
}

function sumInts(values: number[], locale: Locale): string {
  return formatNumber(
    values.reduce((sum, value) => sum + value, 0),
    locale,
    0,
  );
}
